import DataLoader from 'dataloader'
import { Id } from '@/infra/id'
import { collection } from '@/infra/collection'
import LegendSettings from '@/features/jobs/legends/LegendSettings'
import type { FetchLegendSettingsRequest } from '@/features/jobs/api/resources/legend'
import type { LegendsApi } from '@/features/jobs/api/legend-api'
import type { PrimeLegendSettingsProps } from '@/features/jobs'
import { mapLegendSettingResourcesToProps } from '@/features/jobs/legends/legend-settings-mappers'

export class LegendSettingsStore {
  private readonly _cacheKeyFn = (params: FetchLegendSettingsRequest) => {
    return `${params.id}:${params.version}`
  }

  private readonly _loader = new DataLoader(this._fetchManyCore.bind(this), {
    cacheKeyFn: this._cacheKeyFn,
  })

  /**
   * The collection of {@link LegendSettings} models.
   */
  legendSettings = collection({
    create: () => new LegendSettings(),
    getDataId: (data) => `${data.id}:${data.version}`,
    getModelId: (model) => `${model.id}:${model.version}`,
  })

  constructor(private readonly _api: LegendsApi) {}

  /**
   * Primes the {@link LegendSettings} and fetches it in the background.
   */
  prime(props: PrimeLegendSettingsProps) {
    void this.fetch(props.id, props.version)

    return this.legendSettings.set({
      id: props.id,
      version: props.version,
    })
  }

  /**
   * Fetches the {@link LegendSettings} for the given ID and version.
   */
  async fetch(id: Id, version: number) {
    return this._loader.load({ id: Id.assert(id), version })
  }

  /**
   * Fetches the {@link LegendSettings} by given IDs and versions.
   */
  async _fetchManyCore(params: readonly FetchLegendSettingsRequest[]) {
    const resources = await this._api.legendSettings.getMany(params)
    return this.legendSettings.set(
      resources.map(mapLegendSettingResourcesToProps),
    )
  }
}
